import { ref, reactive, toRefs, computed, nextTick, getCurrentInstance } from 'vue'
import { HOST_API } from '@/api/config'
import { isArray, isEmpty, deepCopy, pullKeys } from '@/common/js/utils'
import { queryAllDeveloperConfig } from '@/api/developerConfig.model'
import { getSysMember, getSysMedia, queryAgentList, adAccount } from '@/api/common.model'
import {
  addAccount,
  editAccount,
  queryAccountList,
  updateAccountStatus,
  batchUpdateAccountStatus,
  batchImportAccountData,
  getSpiderMonitorConfig,
  setSpiderMonitorConfig,
  accountDisableTrigger,
  queryCompanyList,
  updateImageUploadManageAccount
} from '@/api/sysmana.model'
import columns from '../data/account_manage_column'

// 1. 全局配置与字典数据 Hook
export const useGlobalData = () => {
  const state = reactive({
    dataSource: {
      agentData: [],
      accountData: [],
      authAppData: [],
      companyData: [],
      accountStatusData: [
        { id: 1, name: '启用中' },
        { id: 0, name: '已禁用' }
      ],
      accountTypeData: [
        { id: 0, name: '普通账号' },
        { id: 1, name: '管家账号' },
        { id: 2, name: '智投账号' }
      ]
    }
  })

  const preload = async () => {
    try {
      const data = await Promise.all([
        queryAgentList(),
        queryCompanyList(),
        queryAllDeveloperConfig(),
        adAccount({ accountType: 1 })
      ])
      const [agentData, companyData, authAppData, accountData] = data
      if (isArray(accountData)) state.dataSource.accountData = accountData

      if (isArray(agentData)) {
        state.dataSource.agentData = agentData.map((ret) => ({
          id: ret.id,
          name: ret.agentName
        }))
      }

      if (isArray(companyData)) {
        state.dataSource.companyData = companyData.map((ret) => ({
          id: ret.id,
          name: ret.companyName
        }))
      }

      if (isArray(authAppData)) {
        state.dataSource.authAppData = authAppData.map((ret) => ({
          id: ret.id,
          name: ret.appName,
          mediaId: ret.mediaId
        }))
      }
    } catch (err) {
      console.log(err)
    }
  }

  return {
    ...toRefs(state),
    preload
  }
}

// 2. 列表数据与分页 Hook
export const useAccountList = () => {
  const state = reactive({
    loading: false,
    pageIndex: 1,
    pageSize: 20,
    total: 0,
    tableList: [],
    tableCol: columns,
    tableHeight: 330,
    params: {},
    sort: undefined,
    order: undefined
  })

  const getData = (filter) => {
    if (filter) state.pageIndex = 1
    let params = {
      pageIndex: state.pageIndex,
      pageSize: state.pageSize,
      sort: state.sort,
      order: state.order
    }

    if (filter) params = Object.assign(params, filter)
    else if (state.params) params = Object.assign(state.params, params)

    // 保存参数
    state.params = JSON.parse(JSON.stringify(params))
    state.loading = true

    queryAccountList(params).then((data) => {
      if (typeof data !== 'symbol') state.loading = false
      if (!data) return

      const { list, page_info } = data
      if (isArray(list) && page_info) {
        const { total_number } = page_info
        state.total = total_number || 0
        state.tableList = list.map((ret) => {
          ret.optLoading = false
          return ret
        })
      } else {
        state.tableList = []
      }
    })
  }

  const changePageIndex = (index) => {
    state.pageIndex = index
    getData()
  }

  const changePageSize = (size) => {
    state.pageSize = size
    state.pageIndex = 1
    getData()
  }

  return {
    ...toRefs(state),
    getData,
    changePageIndex,
    changePageSize
  }
}

// 3. 账号操作 Hook (启用/禁用/导出/模板)
export const useAccountActions = (getData, selectListRef, proxy) => {
  const state = reactive({
    companyModal: false
  })

  const changeStatus = (row) => {
    const { status, id } = row
    const params = {
      accountId: id,
      status
    }
    row.optLoading = true
    updateAccountStatus(params).then((data) => {
      row.optLoading = false
      if (data) {
        proxy.$Message.success('操作成功')
      } else {
        proxy.$Message.error('操作失败！')
      }
    })
  }

  const batchChangeStatus = (status) => {
    const selectList = selectListRef.value
    const params = {
      accountIds: selectList.map((ret) => ret.id).join(','),
      status
    }
    // Note: accessing loading from outside might be tricky if not passed.
    // Assuming loading is handled by the caller or we need to expose a loading state here.
    // In original code, it sets `this.loading = true`.
    // We should probably accept a setLoading callback or shared loading state.
    // For simplicity, let's assume we pass a loading ref or similar.
    // But wait, useAccountList has loading.
    // Let's just return a promise or manage local loading?
    // The original sets `this.loading`. Let's assume we can trigger a refresh which sets loading.
    // Or we should pass the `loading` ref from useAccountList.

    // Better: let's invoke the API and then refresh.
    // We'll use a local loading or assume the UI handles it.
    // Actually, in the original code, `batchChangeStatus` sets `this.loading`.
    // We can accept `loading` as an argument.

    batchUpdateAccountStatus(params).then((data) => {
      if (data) {
        proxy.$Message.success('操作成功')
        getData()
      } else {
        proxy.$Message.error('操作失败！')
      }
    })
  }

  const accountExport = (params) => {
    let url = `${HOST_API}/api/ptom/adAccount/accountOutput?`
    for (const key of Object.keys(params)) {
      url += `${key}=${params[key]}&`
    }
    const selectList = selectListRef.value
    if (isArray(selectList) && selectList.length) {
      url += `ids=${selectList.map((ret) => ret.id).join(',')}`
    }
    window.open(url)
  }

  const templateDown = () => {
    const url = `${HOST_API}/api/ptom/template/downTemplate?templateType=template_ptom_account`
    window.open(url)
  }

  const importExcel = (file) => {
    // Note: Original code sets `this.loading = true`.
    batchImportAccountData({ file }).then((data) => {
      const { successTotal, failTotal } = data
      if (typeof successTotal === 'number' || typeof failTotal === 'number') {
        const url = `${HOST_API}/api/ptom/adAccount/downloadErrorAccount`
        proxy.$Modal.info({
          title: '添加结果',
          content: `成功${data.successTotal}条，失败${data.failTotal}条`,
          okText: data.failTotal ? '下载失败结果' : '完成',
          closable: true,
          onOk: () => {
            if (data.failTotal) {
              window.open(url)
            }
          }
        })
        getData()
      } else {
        proxy.$Message.error('导入数据失败！')
      }
    })
  }

  return {
    ...toRefs(state),
    changeStatus,
    batchChangeStatus,
    accountExport,
    templateDown,
    importExcel
  }
}

// 4. 账号详情/编辑/新增 Hook
export const useAccountModal = (getData, proxy) => {
  const MODAL_TYPE = {
    ADD: 0,
    EDIT: 1,
    VIEW: 2
  }
  const state = reactive({
    accountInfoModal: false,
    accountInfoModalType: MODAL_TYPE.VIEW, // 0: add, 1: edit, 2: view
    accountInfo: {},
    btnLoading: false
  })

  const onAddAccount = () => {
    state.accountInfo = undefined
    state.accountInfoModal = true
    state.accountInfoModalType = MODAL_TYPE.ADD
  }

  const onEditAccount = (row) => {
    // deepCopy and format
    const info = preProcessRow(row)
    state.accountInfo = info
    state.accountInfoModal = true
    state.accountInfoModalType = MODAL_TYPE.EDIT
  }

  const showAccount = (row) => {
    const info = preProcessRow(row)
    state.accountInfo = info
    state.accountInfoModal = true
    state.accountInfoModalType = MODAL_TYPE.VIEW
  }

  const preProcessRow = (row) => {
    // Handle nulls
    const newRow = { ...row }
    for (const key in newRow) {
      if (newRow[key] === null) newRow[key] = ''
    }
    const info = deepCopy(newRow)
    info.opIds = !isEmpty(info.opIds) ? info.opIds.split(',').map((ret) => Number(ret)) : ''
    return info
  }

  const handleAccountEvent = (params) => {
    // 信息展示不做提交操作
    if (state.accountInfoModalType === MODAL_TYPE.VIEW) {
      state.accountInfoModal = false
      return
    }

    params.opIds = isArray(params.opIds) ? params.opIds.join(',') : ''

    if (state.accountInfoModalType === MODAL_TYPE.ADD) {
      state.btnLoading = true
      addAccount(params).then((data) => {
        state.btnLoading = false
        if (data) {
          proxy.$Message.success('新增成功')
          state.accountInfoModal = false
          getData()
        } else {
          proxy.$Message.error('新增失败！')
        }
      })
    } else if (state.accountInfoModalType === MODAL_TYPE.EDIT) {
      const editKeys = [
        'id', 'mediaId', 'adAccount', 'adAccountSimpleName', 'adPwd',
        'advertiserId', 'accountType', 'manageAccountId', 'opIds',
        'rebate', 'finaRebate', 'status', 'agentId', 'settleType',
        'invoiceUnit', 'companyId', 'authAppId', 'signKey', 'secret', 'appId'
      ]
      state.btnLoading = true
      editAccount(pullKeys(editKeys, params)).then((data) => {
        state.btnLoading = false
        if (data) {
          proxy.$Message.success('修改成功')
          state.accountInfoModal = false
          getData()
        } else {
          proxy.$Message.error('修改失败！')
        }
      })
    }
  }

  return {
    ...toRefs(state),
    onAddAccount,
    onEditAccount,
    showAccount,
    handleAccountEvent
  }
}

// 5. 自动禁用监控 Hook
export const useSpiderMonitor = (proxy) => {
  const state = reactive({
    showAccountDisableModal: false,
    triggerLoading: false,
    monitorBtnLoading: false
    // Note: ref="accountForbidRef" needs to be handled in setup
  })

  // We need to pass the ref or return a function that sets the form
  // Since refs are bound in template, we can access them via an exposed function or pass the ref object
  // Here we assume the component exposes a way to get the ref instance

  const openAccountDisableModal = (accountForbidRef) => {
    // Need loading state? Original sets this.loading = true.
    getSpiderMonitorConfig().then((data) => {
      if (data) {
        const { email_type2, opIds_monitor2, days, hours } = JSON.parse(data)
        const toNumber = (ret) => Number(ret)
        const info = {
          days: days ? days.split(',').map(toNumber) : [],
          hours: days ? hours.split(',').map(toNumber) : [],
          alertType: Number(email_type2),
          alertOpIds: opIds_monitor2 ? opIds_monitor2.split(',').map(toNumber) : []
        }
        state.showAccountDisableModal = true
        nextTick(() => {
          if (accountForbidRef.value) {
            accountForbidRef.value.setForm(info)
          }
        })
      }
    })
  }

  const onAccountForbidConfigConfirm = (accountForbidRef) => {
    if (!accountForbidRef.value) return
    const params = accountForbidRef.value.getForm()

    state.monitorBtnLoading = true
    setSpiderMonitorConfig({ info: JSON.stringify(params) }).then((data) => {
      state.monitorBtnLoading = false
      if (data) {
        proxy.$Message.success('操作成功')
        state.showAccountDisableModal = false
      }
    })
  }

  const onAccountForbidManualTrigger = () => {
    state.triggerLoading = true
    accountDisableTrigger()
      .then((data) => {
        if (data) {
          proxy.$Message.success('已手动触发，程序执行完毕大约需要5分钟')
        }
      })
      .finally(() => {
        state.triggerLoading = false
      })
  }

  return {
    ...toRefs(state),
    openAccountDisableModal,
    onAccountForbidConfigConfirm,
    onAccountForbidManualTrigger
  }
}

// 6. 清理账号 Hook
export const useClearAccounts = (proxy) => {
  const state = reactive({
    showClearMsg: false,
    clearErrorData: [],
    clearFileImportLoading: false,
    uploadUrl: HOST_API + '/api/ptom/adAccount/importAccountClear'
  })

  const handleUploadSuccess = (res) => {
    state.showClearMsg = true
    if (res.code !== 200) {
      state.clearErrorData = res.data || []
    }
  }

  const handleUploadError = (err) => {
    try {
      const { error, path } = JSON.parse(err.message)
      state.clearFileImportLoading = false
      proxy.$Message.error(path + ' ' + error)
    } catch (e) {
      console.log('handleUploadError eeeee output: ', e)
    }
  }

  const handleUploadChange = ({ status }) => {
    switch (status) {
      case 'ready':
        state.clearFileImportLoading = true
        break
      case 'success':
        setTimeout(() => (state.clearFileImportLoading = false), 500)
        break
      default:
        break
    }
  }

  const onClearMsgModalClose = () => {
    state.showClearMsg = false
    state.clearErrorData = []
  }

  return {
    ...toRefs(state),
    handleUploadSuccess,
    handleUploadError,
    handleUploadChange,
    onClearMsgModalClose
  }
}

// 7. 媒体素材管理账号配置 Hook
export const useMaterialAccountConfig = (proxy) => {
  const state = reactive({
    showMaterialConfigModal: false,
    configLoading: false
  })

  const openMaterialConfigModal = async () => {
    state.showMaterialConfigModal = true
  }

  const saveMaterialAccountConfig = async (data) => {
    state.configLoading = true
    try {
      const res = await updateImageUploadManageAccount(data)
      if (res) {
        proxy.$Message.success('保存成功')
        state.showMaterialConfigModal = false
      } else {
        proxy.$Message.error('保存失败')
      }
    } catch (e) {
      proxy.$Message.error('保存出错')
    } finally {
      state.configLoading = false
    }
  }

  return {
    ...toRefs(state),
    openMaterialConfigModal,
    saveMaterialAccountConfig
  }
}
